import { redactClerkTicket } from './invitation-auth-links'
/**
 * Web Vitals Real User Monitoring (RUM) for AxiomGO Chat.
 *
 * Uses the official @grafana/faro-web-sdk to deliver metrics in the Faro
 * schema that the `/collect/<id>` endpoint expects. The previous custom
 * sendBeacon + `{ samples: [] }` payload was silently rejected (schema
 * mismatch) — this replaces it entirely.
 *
 * Web Vitals coverage via getWebInstrumentations() → WebVitalsInstrumentation:
 *   LCP · FCP · CLS · INP · TTFB  (all 5 Core Web Vitals)
 *
 * PII rules (non-negotiable):
 *  - sessionTracking: { enabled: false } — no random session IDs stored.
 *  - No userId, tenantId, email, conversationId in meta or beforeSend.
 *  - view.name = pathname only (Faro default: window.location.pathname).
 *  - beforeSend strips any unexpected top-level meta keys defensively.
 *  - No TracingInstrumentation — saves ~30KB gzip.
 *
 * Call initWebVitals() once after createRoot().render() in main.tsx.
 * The function is idempotent: a second call is a no-op.
 */

import { initializeFaro, getWebInstrumentations, type TransportItem } from '@grafana/faro-web-sdk'

// ─── Module-level state ───────────────────────────────────────────────────────

/** Guards against double-registration across HMR reloads and test runs. */
let initialized = false

// ─── PII guard ───────────────────────────────────────────────────────────────

/**
 * Allowed top-level keys on TransportItem.meta.
 * Any key NOT in this set is stripped before the item leaves the browser.
 * This is a defence-in-depth layer — Faro itself does not send PII by default
 * when user/session tracking is disabled, but we sanitise regardless.
 */
const ALLOWED_META_KEYS = new Set(['app', 'browser', 'page', 'sdk', 'view'])

/**
 * beforeSend hook — strips any unexpected meta keys (e.g. user, session).
 * Returning null would drop the item entirely; we return the sanitised item.
 */
/** Redacts invitation paths in metadata and payload without changing unrelated values. */
function redactInvitationPaths(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactClerkTicket(value).replace(
      /((?:\/|%2f|%252f)invitations(?:\/|%2f|%252f))[a-z0-9_-]+/gi,
      '$1[Redacted]',
    )
  }
  if (Array.isArray(value)) {
    const redacted = value.map(redactInvitationPaths)
    return redacted.some((item, index) => item !== value[index]) ? redacted : value
  }
  if (!value || typeof value !== 'object') return value
  const entries = Object.entries(value)
  const redacted = entries.map(
    ([key, item]) =>
      [key, key === '__clerk_ticket' ? '[Redacted]' : redactInvitationPaths(item)] as const,
  )
  return redacted.some((entry, index) => entry[1] !== entries[index][1])
    ? Object.fromEntries(redacted)
    : value
}

function beforeSend(item: TransportItem): TransportItem {
  const sanitisedMeta = Object.fromEntries(
    Object.entries(item.meta).filter(([key]) => ALLOWED_META_KEYS.has(key)),
  )
  return redactInvitationPaths({ ...item, meta: sanitisedMeta }) as TransportItem
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Initialise Faro RUM.
 *
 * Delegates to @grafana/faro-web-sdk which:
 *  - Posts Faro schema payloads ({ meta, payload: { measurements } }) to the
 *    Grafana Cloud collector endpoint — no more silent 4xx from schema mismatch.
 *  - Captures LCP/FCP/CLS/INP/TTFB via WebVitalsInstrumentation.
 *  - Captures console.error and unhandledRejection via default instrumentations.
 *  - Does NOT send tracing spans (no TracingInstrumentation).
 *
 * Idempotent — calling twice is a no-op (module-level `initialized` guard).
 * Must be called once from main.tsx AFTER createRoot().render().
 * Must NOT be called from inside a React component.
 */
export function initWebVitals(): void {
  if (initialized) return

  const endpoint = import.meta.env['VITE_RUM_ENDPOINT'] as string | undefined
  if (!endpoint) return // dev mode / not yet configured — silent no-op

  // Card #190: Faro endpoint set on develop spammed 197 console errors per session
  // because the Grafana Cloud app key wasn't whitelisted for the develop origin.
  // Require explicit opt-in via VITE_ENABLE_RUM=true to suppress noise on
  // non-production environments while keeping production telemetry intact.
  const enableRum = import.meta.env['VITE_ENABLE_RUM'] as string | undefined
  if (enableRum !== 'true') return

  initialized = true

  initializeFaro({
    url: endpoint,
    app: {
      name: 'axiomgo-chat',
      version: (import.meta.env['VITE_APP_VERSION'] as string | undefined) ?? '0.0.0',
      environment: import.meta.env['MODE'] as string,
    },
    sessionTracking: {
      enabled: false, // no random session IDs — PII boundary
    },
    instrumentations: getWebInstrumentations(),
    beforeSend,
  })
}

// ─── Test-only exports ────────────────────────────────────────────────────────
// These are exported for unit testing and must not be used in application code.

/**
 * Reset the `initialized` flag so tests can call initWebVitals() again cleanly.
 * Faro does not expose a public reset API — only the guard flag is cleared here.
 * ONLY for testing.
 */
export function _resetFaroForTesting(): void {
  initialized = false
}
